import { useEffect, useRef, useState } from 'react';
import Checkbox, { type ICheckboxProps } from './Checkbox';

export type IRangeCheckboxProps = ICheckboxProps;

/**
 * A checkbox for range selection, where a click does not always flip the box it landed on.
 *
 * `Checkbox` renders from internal state that its own change handler sets from the browser's native
 * toggle, and it only re-reads `checked` when that prop *changes*. Range selection routinely re-asserts
 * the same `checked` value for the clicked box — shrinking a range back to its anchor leaves the anchor
 * selected — and the box would then keep showing the native toggle rather than the real state.
 *
 * Remounting it restores the intended state. That happens only when the prop did not change, so an
 * ordinary toggle keeps its focus.
 *
 * Use it with `useCheckboxRangeSelection`; a plain `Checkbox` is right everywhere else.
 */
const RangeCheckbox = ({ onClick, checked, ...props }: IRangeCheckboxProps) => {
  const [mountKey, setMountKey] = useState(0);
  const [clickCount, setClickCount] = useState(0);
  const lastClickCountRef = useRef(clickCount);
  const lastCheckedRef = useRef(checked);

  useEffect(() => {
    const wasClicked = lastClickCountRef.current !== clickCount;

    lastClickCountRef.current = clickCount;

    if (wasClicked && lastCheckedRef.current === checked) {
      setMountKey((key) => key + 1);
    }

    lastCheckedRef.current = checked;
  }, [clickCount, checked]);

  return (
    <Checkbox
      { ...props }
      key={ mountKey }
      checked={ checked }
      onClick={ (event) => {
        // Renders after every click, so the effect above can always compare `checked` across it.
        setClickCount((count) => count + 1);
        onClick?.(event);
      } }
    />
  );
};

export default RangeCheckbox;
